/**
 * IANA timezone helpers. We use the platform's own list via
 * Intl.supportedValuesOf("timeZone") (well-supported in modern browsers),
 * and compute the current UTC offset for display next to each name.
 */

export const DEFAULT_TZ = "Asia/Dubai";
export const MT5_DEFAULT_TZ = "Europe/Istanbul";

/** All IANA identifiers, alphabetically. Includes "UTC" (+00:00). */
export function listTimezones(): string[] {
  const anyIntl = Intl as unknown as {
    supportedValuesOf?: (key: string) => string[];
  };
  const list = anyIntl.supportedValuesOf?.("timeZone");
  if (list && list.length) return list;
  return FALLBACK_LIST;
}

/**
 * The zone the device is actually in, as reported by the browser. This reflects
 * the OS setting, which on phones and most desktops tracks physical location --
 * so it is the right answer for "detect my timezone" without asking for the
 * geolocation permission. Returns null when the platform reports nothing.
 */
export function detectTimezone(): string | null {
  try {
    const tz = Intl.DateTimeFormat().resolvedOptions().timeZone;
    return tz ? tz : null;
  } catch {
    return null;
  }
}

/** Current UTC offset for a zone as "+HH:MM" / "-HH:MM". */
export function tzOffset(tz: string, at: Date = new Date()): string {
  try {
    const dtf = new Intl.DateTimeFormat("en-US", {
      timeZone: tz,
      timeZoneName: "shortOffset",
      hour: "numeric",
    });
    // Format like "3 AM GMT+4" — extract the last "GMT±H[:MM]" piece.
    const parts = dtf.formatToParts(at);
    const off = parts.find((p) => p.type === "timeZoneName")?.value ?? "";
    return normalizeOffset(off);
  } catch {
    return "";
  }
}

function normalizeOffset(raw: string): string {
  // Examples: "GMT", "GMT+4", "GMT-3:30", "UTC", "UTC+05:30"
  const stripped = raw.replace(/^(GMT|UTC)/i, "").trim();
  if (stripped === "" || stripped === "+0" || stripped === "-0") return "+00:00";
  const m = stripped.match(/^([+-])(\d{1,2})(?::?(\d{2}))?$/);
  if (!m) return raw;
  const sign = m[1];
  const hh = m[2].padStart(2, "0");
  const mm = m[3] ? m[3] : "00";
  return `${sign}${hh}:${mm}`;
}

/** Utility for a picker: label like "Asia/Dubai (+04:00)". */
export function tzLabel(tz: string, at?: Date): string {
  const off = tzOffset(tz, at);
  return off ? `${tz} (${off})` : tz;
}

// Minimal fallback for browsers without Intl.supportedValuesOf — very sparse
// but covers the majors so the picker still works.
const FALLBACK_LIST = [
  "UTC",
  "Africa/Cairo",
  "Africa/Johannesburg",
  "Africa/Lagos",
  "America/Anchorage",
  "America/Bogota",
  "America/Buenos_Aires",
  "America/Chicago",
  "America/Denver",
  "America/Los_Angeles",
  "America/Mexico_City",
  "America/New_York",
  "America/Sao_Paulo",
  "America/Toronto",
  "America/Vancouver",
  "Asia/Baghdad",
  "Asia/Bangkok",
  "Asia/Dubai",
  "Asia/Hong_Kong",
  "Asia/Jakarta",
  "Asia/Karachi",
  "Asia/Kolkata",
  "Asia/Kuala_Lumpur",
  "Asia/Kuwait",
  "Asia/Manila",
  "Asia/Riyadh",
  "Asia/Seoul",
  "Asia/Shanghai",
  "Asia/Singapore",
  "Asia/Taipei",
  "Asia/Tehran",
  "Asia/Tokyo",
  "Australia/Melbourne",
  "Australia/Perth",
  "Australia/Sydney",
  "Europe/Amsterdam",
  "Europe/Athens",
  "Europe/Berlin",
  "Europe/Brussels",
  "Europe/Bucharest",
  "Europe/Istanbul",
  "Europe/London",
  "Europe/Madrid",
  "Europe/Moscow",
  "Europe/Paris",
  "Europe/Rome",
  "Europe/Stockholm",
  "Europe/Vienna",
  "Pacific/Auckland",
  "Pacific/Honolulu",
];
